import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  CloseRfiRequest,
  CreateRfiRequest,
  Rfi,
  RfiCreateResult,
  RfiStatus,
} from './types';

/**
 * RFI / outreach composer (F-IS-04) — PS-3 cross-track contract. Track A builds these in parallel;
 * the SPA degrades to teaching empty states (errors.ts `isNotAvailable`) when absent.
 *
 * The composer assembles the subject/body DETERMINISTICALLY on the client from the asks (no AI
 * drafting); the analyst edits the exact text before the two-step send gate. `/send` may answer
 * 422 `CHANNEL_UNBOUND` when the hub has no bound provider for the chosen communication channel.
 */

const enc = (s: string) => encodeURIComponent(s);

/** POST /v1/rfi — create a DRAFT RFI from the composed asks + text. */
export async function createRfi(body: CreateRfiRequest, idemKey?: string): Promise<RfiCreateResult> {
  const { data } = await apiClient.post<ApiEnvelope<RfiCreateResult>>(
    '/v1/rfi',
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** POST /v1/rfi/{id}/send — dispatch a DRAFT (422 `CHANNEL_UNBOUND` if the channel has no provider). */
export async function sendRfi(rfiId: string, idemKey?: string): Promise<Rfi> {
  const { data } = await apiClient.post<ApiEnvelope<Rfi>>(
    `/v1/rfi/${enc(rfiId)}/send`,
    {},
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** GET /v1/rfi/{id} — a single RFI. */
export async function getRfi(rfiId: string): Promise<Rfi> {
  const { data } = await apiClient.get<ApiEnvelope<Rfi>>(`/v1/rfi/${enc(rfiId)}`);
  return unwrap(data);
}

/** GET /v1/rfi?entityId=&status= — RFIs for an entity (the entity-360 RFI tab). */
export async function listRfi(entityId?: string, status?: RfiStatus): Promise<Rfi[]> {
  const { data } = await apiClient.get<ApiEnvelope<Rfi[]>>('/v1/rfi', {
    params: { entityId, status },
  });
  return unwrap(data);
}

/** POST /v1/rfi/{id}/close — close out with an outcome. */
export async function closeRfi(
  rfiId: string,
  body: CloseRfiRequest,
  idemKey?: string,
): Promise<Rfi> {
  const { data } = await apiClient.post<ApiEnvelope<Rfi>>(
    `/v1/rfi/${enc(rfiId)}/close`,
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}
